import type { AccountBlob } from './api';

export type Operator =
  | 'contains'
  | 'doesNotContain'
  | 'equals'
  | 'doesNotEqual'
  | 'isOneOf'
  | 'isNotOneOf'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'before'
  | 'after'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'isTrue'
  | 'isFalse'
  | 'isNotTrue';

export type Combinator = 'AND' | 'OR';

export type FieldType = 'string' | 'number' | 'boolean' | 'date' | 'distance';

/** The radius rule's pseudo-field: within N miles of a point. */
export const WITHIN_FIELD = '$within';
export const WITHIN_LABEL = 'Within … miles of a point';

export interface FilterRule {
  id: string;
  field: string;
  operator: Operator;
  value: string;
  /** Only on the radius rule. */
  center?: { lat: number; lng: number };
}

export interface OperatorDef {
  op: Operator;
  label: string;
  needsValue: boolean;
}

const BUILT_IN_FIELDS: Record<string, FieldType> = {
  name: 'string',
  address: 'string',
  city: 'string',
  state: 'string',
  zipCode: 'string',
  [WITHIN_FIELD]: 'distance',
};

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T|$)/;

export function inferType(field: string, accounts: AccountBlob[]): FieldType {
  if (BUILT_IN_FIELDS[field]) return BUILT_IN_FIELDS[field];

  let numberCount = 0;
  let booleanCount = 0;
  let dateCount = 0;
  let stringCount = 0;
  let seen = 0;
  for (const a of accounts) {
    const v = a.fields[field];
    if (v == null) continue;
    seen++;
    if (typeof v === 'number') numberCount++;
    else if (typeof v === 'boolean') booleanCount++;
    else if (typeof v === 'string' && DATE_REGEX.test(v)) dateCount++;
    else stringCount++;
    if (seen >= 50) break;
  }
  if (seen === 0) return 'string';
  if (numberCount === seen) return 'number';
  if (booleanCount === seen) return 'boolean';
  if (dateCount === seen) return 'date';
  return 'string';
}

export const OPERATORS_BY_TYPE: Record<FieldType, OperatorDef[]> = {
  string: [
    { op: 'contains', label: 'contains', needsValue: true },
    { op: 'doesNotContain', label: 'does not contain', needsValue: true },
    { op: 'equals', label: 'equals', needsValue: true },
    { op: 'doesNotEqual', label: 'does not equal', needsValue: true },
    { op: 'isOneOf', label: 'is one of (comma-separated)', needsValue: true },
    { op: 'isNotOneOf', label: 'is not one of (comma-separated)', needsValue: true },
    { op: 'isEmpty', label: 'is empty', needsValue: false },
    { op: 'isNotEmpty', label: 'is not empty', needsValue: false },
  ],
  number: [
    { op: 'equals', label: '=', needsValue: true },
    { op: 'doesNotEqual', label: '≠', needsValue: true },
    { op: 'gt', label: '>', needsValue: true },
    { op: 'gte', label: '≥', needsValue: true },
    { op: 'lt', label: '<', needsValue: true },
    { op: 'lte', label: '≤', needsValue: true },
    { op: 'isEmpty', label: 'is empty', needsValue: false },
    { op: 'isNotEmpty', label: 'is not empty', needsValue: false },
  ],
  boolean: [
    { op: 'isTrue', label: 'is true', needsValue: false },
    { op: 'isFalse', label: 'is false', needsValue: false },
    { op: 'isNotTrue', label: 'is not true (false or blank)', needsValue: false },
    { op: 'isEmpty', label: 'is empty', needsValue: false },
    { op: 'isNotEmpty', label: 'is not empty', needsValue: false },
  ],
  date: [
    { op: 'before', label: 'before', needsValue: true },
    { op: 'after', label: 'after', needsValue: true },
    { op: 'isEmpty', label: 'is empty', needsValue: false },
    { op: 'isNotEmpty', label: 'is not empty', needsValue: false },
  ],
  distance: [{ op: 'lte', label: 'within (miles)', needsValue: true }],
};

function readField(account: AccountBlob, field: string): unknown {
  switch (field) {
    case 'id': return account.id;
    case 'name': return account.name;
    case 'address': return account.address;
    case 'city': return account.city;
    case 'state': return account.state;
    case 'zipCode': return account.zipCode;
    default: return account.fields[field];
  }
}

const EARTH_MI = 3958.7613;
export function haversineMi(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_MI * Math.asin(Math.min(1, Math.sqrt(s)));
}

const listOf = (v: string) => v.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);

export function evaluateRule(account: AccountBlob, rule: FilterRule): boolean {
  if (rule.field === WITHIN_FIELD) {
    if (!rule.center) return false;
    return haversineMi(account.lat, account.lng, rule.center.lat, rule.center.lng) <= Number(rule.value);
  }
  const raw = readField(account, rule.field);
  const isNullish = raw == null || raw === '';

  switch (rule.operator) {
    case 'isEmpty': return isNullish;
    case 'isNotEmpty': return !isNullish;
    case 'isTrue': return raw === true;
    case 'isFalse': return raw === false;
    case 'isNotTrue': return raw !== true;
  }

  if (isNullish) return false;

  const strVal = String(raw).toLowerCase();
  const ruleVal = rule.value.toLowerCase();

  switch (rule.operator) {
    case 'contains': return strVal.includes(ruleVal);
    case 'doesNotContain': return !strVal.includes(ruleVal);
    case 'equals': return strVal === ruleVal;
    case 'doesNotEqual': return strVal !== ruleVal;
    case 'isOneOf': return listOf(rule.value).includes(strVal.trim());
    case 'isNotOneOf': return !listOf(rule.value).includes(strVal.trim());
    case 'gt': return Number(raw) > Number(rule.value);
    case 'gte': return Number(raw) >= Number(rule.value);
    case 'lt': return Number(raw) < Number(rule.value);
    case 'lte': return Number(raw) <= Number(rule.value);
    case 'before': {
      const a = new Date(String(raw)).getTime();
      const b = new Date(rule.value).getTime();
      return Number.isFinite(a) && Number.isFinite(b) && a < b;
    }
    case 'after': {
      const a = new Date(String(raw)).getTime();
      const b = new Date(rule.value).getTime();
      return Number.isFinite(a) && Number.isFinite(b) && a > b;
    }
    default: return false;
  }
}

export function accountMatches(
  account: AccountBlob,
  rules: FilterRule[],
  combinator: Combinator,
): boolean {
  const activeRules = rules.filter(isRuleComplete);
  if (activeRules.length === 0) return true;
  if (combinator === 'AND') return activeRules.every((r) => evaluateRule(account, r));
  return activeRules.some((r) => evaluateRule(account, r));
}

const NO_VALUE_OPS: Operator[] = ['isEmpty', 'isNotEmpty', 'isTrue', 'isFalse', 'isNotTrue'];

export function isRuleComplete(rule: FilterRule): boolean {
  if (!rule.field) return false;
  if (rule.field === WITHIN_FIELD) {
    return !!rule.center && Number.isFinite(rule.center.lat) && Number.isFinite(rule.center.lng) && Number(rule.value) > 0;
  }
  const needsValue = !NO_VALUE_OPS.includes(rule.operator);
  if (needsValue && rule.value.trim() === '') return false;
  return true;
}

export function defaultOperatorFor(type: FieldType): Operator {
  return OPERATORS_BY_TYPE[type][0].op;
}

export function newRule(field: string, type: FieldType): FilterRule {
  return {
    id: `r_${Math.random().toString(36).slice(2, 10)}`,
    field,
    operator: defaultOperatorFor(type),
    value: '',
  };
}

/** Encode / decode a filter for the URL (`?rules=`), base64url of JSON. The CRM's modules build these links. */
export function encodeRules(rules: FilterRule[], combinator: Combinator): string {
  const json = JSON.stringify({ rules, combinator });
  return btoa(unescape(encodeURIComponent(json))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeRules(param: string): { rules: FilterRule[]; combinator: Combinator } | null {
  try {
    const b64 = param.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(escape(atob(b64)));
    const doc = JSON.parse(json) as { rules?: unknown; combinator?: unknown };
    if (!Array.isArray(doc.rules)) return null;
    const rules = (doc.rules as Partial<FilterRule>[])
      .filter((r) => r && typeof r.field === 'string' && typeof r.operator === 'string')
      .map((r, i) => ({ id: typeof r.id === 'string' && r.id ? r.id : `u_${i}`, field: r.field!, operator: r.operator as Operator, value: typeof r.value === 'string' ? r.value : String(r.value ?? ''), ...(r.center ? { center: r.center } : {}) }));
    return { rules, combinator: doc.combinator === 'OR' ? 'OR' : 'AND' };
  } catch {
    return null;
  }
}
